import { Link } from 'react-router-dom'
import { useLanguage } from '@/i18n/LanguageContext'
import { resolveNavHref } from '@/components/layout/resolveNavHref'

/**
 * Bottom-fixed consent notice, mounted globally in App.tsx (same level as
 * ViberBubble, so it persists across every page under LangRoot).
 *
 * Gates real behavior: Accept loads Google Analytics (via
 * useCookieConsent.ts -> lib/analytics.ts) and Decline never loads it. The
 * only thing stored regardless of choice is the `ea-lang` localStorage key
 * (see LanguageContext.tsx), which isn't a tracking cookie.
 *
 * Consent state is lifted to App.tsx and passed in as props (rather than
 * kept as local state here) so ViberBubble can share the exact same state
 * instance — otherwise dismissing the banner wouldn't notify a separate
 * ViberBubble instance, and the bubble would stay shifted up (see its own
 * comment) until the next page load.
 */
export function CookieConsent({ visible, dismiss }: { visible: boolean; dismiss: (choice: 'accepted' | 'declined') => void }) {
  const { t, lang } = useLanguage()

  if (!visible) return null

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 md:px-6 md:pb-6">
      <div className="mx-auto flex max-w-3xl flex-col items-start gap-3 rounded-lg border border-white/10 bg-background-elevated/95 p-4 shadow-[0_8px_30px_rgba(0,0,0,0.4)] backdrop-blur-glass sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:p-5">
        <p className="text-xs leading-relaxed text-foreground-muted sm:text-sm">
          {t('cookie-banner-text')}{' '}
          <Link to={resolveNavHref('/privacy', lang)} className="text-accent-fire underline underline-offset-2 hover:text-accent-amber-light">
            {t('cookie-banner-link')}
          </Link>
          .
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => dismiss('declined')}
            className="rounded-pill border border-white/15 px-4 py-2 text-xs font-semibold text-foreground-muted transition-colors hover:border-white/25 hover:text-foreground"
          >
            {t('cookie-decline')}
          </button>
          <button
            type="button"
            onClick={() => dismiss('accepted')}
            className="rounded-pill bg-gradient-to-r from-accent-amber-light to-accent-amber px-4 py-2 text-xs font-bold uppercase tracking-wide text-accent-amber-foreground transition-transform hover:scale-105"
          >
            {t('cookie-accept')}
          </button>
        </div>
      </div>
    </div>
  )
}
